import React, { useEffect, useState } from "react";
import { api, formatINR, formatDate, formatApiError } from "../lib/api";
import { PageHeader, StatusBadge, LoadingRows, EmptyState } from "../components/common";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import { CreditCard, Download, IndianRupee } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "../lib/auth";

export default function Subscriptions() {
  const { user } = useAuth();
  const [plans, setPlans] = useState([]);
  const [subs, setSubs] = useState([]);
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    const [p, s, i] = await Promise.all([
      api.get("/plans"), api.get("/subscriptions", { params: { limit: 100 } }), api.get("/invoices", { params: { limit: 100 } }),
    ]);
    setPlans(p.data.items); setSubs(s.data.items); setInvoices(i.data.items);
    setLoading(false);
  };
  useEffect(() => { load(); }, []);

  const pay = async (inv) => {
    try { await api.post(`/invoices/${inv.id}/pay`); toast.success(`Paid via PhonePe · ${formatINR(inv.total)}`); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const downloadInvoice = (inv) => {
    const html = `<!doctype html><html><head><title>${inv.invoice_number}</title>
    <style>body{font-family:sans-serif;padding:32px;color:#111}h1{color:#2563EB}table{width:100%;border-collapse:collapse;margin-top:24px}td,th{border:1px solid #E2E8F0;padding:10px;text-align:left}th{background:#F8FAFC}.big{font-size:28px;font-weight:600}</style>
    </head><body>
    <div style="display:flex;justify-content:space-between;align-items:flex-start">
      <div><h1 style="margin:0">RushGro</h1><div>Power Backup as a Service</div></div>
      <div style="text-align:right"><div style="font-weight:600">Invoice</div><div>${inv.invoice_number}</div><div>${formatDate(inv.created_at)}</div></div>
    </div>
    <table><tr><th>Description</th><th>Amount</th></tr>
    <tr><td>${inv.plan_name} — Monthly Subscription</td><td>${formatINR(inv.subtotal)}</td></tr>
    <tr><td>GST (18%)</td><td>${formatINR(inv.gst_amount)}</td></tr>
    <tr><td><b>Total</b></td><td class="big">${formatINR(inv.total)}</td></tr>
    </table>
    <p style="margin-top:24px"><b>Status:</b> ${inv.status.toUpperCase()} · <b>Due:</b> ${formatDate(inv.due_date)}</p>
    <p style="color:#64748B;font-size:12px;margin-top:48px">Thank you for choosing RushGro. Powered by PhonePe.</p>
    </body></html>`;
    const w = window.open("", "_blank"); w.document.write(html); w.document.close(); w.focus(); setTimeout(() => w.print(), 500);
  };

  if (loading) return <LoadingRows rows={6} />;

  return (
    <>
      <PageHeader title="Subscriptions & Billing" description="FlexiPod monthly subscriptions, GST-inclusive invoicing, and payments via PhonePe." />

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {plans.map((p) => (
          <Card key={p.id} className="p-6 rounded-xl border">
            <div className="flex items-start justify-between">
              <div>
                <div className="text-xs uppercase tracking-widest text-blue-600">{p.capacity_kw} kW Plan</div>
                <h3 className="font-heading text-xl font-semibold mt-1">{p.name}</h3>
                <p className="text-sm text-muted-foreground mt-1">{p.description}</p>
              </div>
              <CreditCard className="w-5 h-5 text-muted-foreground" />
            </div>
            <div className="mt-4 flex items-baseline gap-1">
              <span className="font-heading text-3xl font-semibold">{formatINR(p.monthly_price)}</span>
              <span className="text-sm text-muted-foreground">/ month</span>
            </div>
            <div className="text-xs text-muted-foreground mt-1">+ {p.gst_percent}% GST · billed monthly</div>
          </Card>
        ))}
      </div>

      <Tabs defaultValue="subs">
        <TabsList>
          <TabsTrigger value="subs" data-testid="tab-subs">Active Subscriptions</TabsTrigger>
          <TabsTrigger value="inv" data-testid="tab-invoices">Invoices</TabsTrigger>
        </TabsList>
        <TabsContent value="subs">
          <Card className="rounded-xl">
            {subs.length === 0 ? <div className="p-6"><EmptyState title="No subscriptions" /></div> : (
              <Table>
                <TableHeader><TableRow>
                  <TableHead>Subscription</TableHead><TableHead>Plan</TableHead><TableHead>Next Billing</TableHead><TableHead>Status</TableHead>
                </TableRow></TableHeader>
                <TableBody>
                  {subs.map((s) => {
                    const plan = plans.find((p) => p.id === s.plan_id);
                    return <TableRow key={s.id}>
                      <TableCell className="font-mono text-xs">{s.id.slice(0, 10)}…</TableCell>
                      <TableCell>{plan?.name || s.plan_id}</TableCell>
                      <TableCell>{formatDate(s.next_billing_date)}</TableCell>
                      <TableCell><StatusBadge status={s.status} /></TableCell>
                    </TableRow>;
                  })}
                </TableBody>
              </Table>
            )}
          </Card>
        </TabsContent>
        <TabsContent value="inv">
          <Card className="rounded-xl">
            {invoices.length === 0 ? <div className="p-6"><EmptyState title="No invoices" /></div> : (
              <Table>
                <TableHeader><TableRow>
                  <TableHead>Invoice #</TableHead><TableHead>Plan</TableHead><TableHead>Total</TableHead>
                  <TableHead>Due</TableHead><TableHead>Status</TableHead><TableHead className="text-right">Actions</TableHead>
                </TableRow></TableHeader>
                <TableBody>
                  {invoices.map((inv) => <TableRow key={inv.id}>
                    <TableCell className="font-mono text-xs">{inv.invoice_number}</TableCell>
                    <TableCell>{inv.plan_name}</TableCell>
                    <TableCell className="font-medium">{formatINR(inv.total)}</TableCell>
                    <TableCell>{formatDate(inv.due_date)}</TableCell>
                    <TableCell><StatusBadge status={inv.status} /></TableCell>
                    <TableCell className="text-right">
                      <Button variant="ghost" size="sm" onClick={() => downloadInvoice(inv)} data-testid={`invoice-download-${inv.id}`}>
                        <Download className="w-4 h-4 mr-1" /> PDF
                      </Button>
                      {inv.status === "pending" && (user.role === "super_admin" || user.role === "brand_admin") && (
                        <Button size="sm" className="bg-blue-600 hover:bg-blue-700 text-white ml-1" onClick={() => pay(inv)} data-testid={`invoice-pay-${inv.id}`}>
                          <IndianRupee className="w-4 h-4 mr-1" /> Pay
                        </Button>
                      )}
                    </TableCell>
                  </TableRow>)}
                </TableBody>
              </Table>
            )}
          </Card>
        </TabsContent>
      </Tabs>
    </>
  );
}
